/**
 * skeletonConstants.js — Skeleton structure definitions for 3D visualization
 *
 * Defines the hierarchical bone structure of the human skeleton.
 * Supports multiple naming conventions from different APIs:
 *   - Azure Kinect (canonical): HandLeft, ShoulderRight, Pelvis, etc.
 *   - Unity/Humanoid: LeftHand, RightShoulder, Hips, etc.
 *   - Kinect v2 SDK: SpineBase, SpineMid, SpineShoulder, etc.
 *
 * The canonical names are Azure Kinect style. All other naming conventions
 * are mapped via JOINT_ALIASES for bone rendering and color coding.
 */

/**
 * Bone connections defining the skeleton structure.
 * Each bone connects two joints (from → to) using CANONICAL names.
 * Based on Azure Kinect Body Tracking skeleton topology.
 */
export const SKELETON_BONES = [
    // Spine chain
    { from: 'Pelvis', to: 'SpineNaval', region: 'torso' },
    { from: 'SpineNaval', to: 'SpineChest', region: 'torso' },
    { from: 'SpineChest', to: 'Neck', region: 'torso' },
    { from: 'Neck', to: 'Head', region: 'head' },

    // Left arm
    { from: 'SpineChest', to: 'ClavicleLeft', region: 'leftArm' },
    { from: 'ClavicleLeft', to: 'ShoulderLeft', region: 'leftArm' },
    { from: 'ShoulderLeft', to: 'ElbowLeft', region: 'leftArm' },
    { from: 'ElbowLeft', to: 'WristLeft', region: 'leftArm' },
    { from: 'WristLeft', to: 'HandLeft', region: 'leftArm' },
    { from: 'HandLeft', to: 'HandTipLeft', region: 'leftArm' },
    { from: 'WristLeft', to: 'ThumbLeft', region: 'leftArm' },

    // Right arm
    { from: 'SpineChest', to: 'ClavicleRight', region: 'rightArm' },
    { from: 'ClavicleRight', to: 'ShoulderRight', region: 'rightArm' },
    { from: 'ShoulderRight', to: 'ElbowRight', region: 'rightArm' },
    { from: 'ElbowRight', to: 'WristRight', region: 'rightArm' },
    { from: 'WristRight', to: 'HandRight', region: 'rightArm' },
    { from: 'HandRight', to: 'HandTipRight', region: 'rightArm' },
    { from: 'WristRight', to: 'ThumbRight', region: 'rightArm' },

    // Left leg
    { from: 'Pelvis', to: 'HipLeft', region: 'leftLeg' },
    { from: 'HipLeft', to: 'KneeLeft', region: 'leftLeg' },
    { from: 'KneeLeft', to: 'AnkleLeft', region: 'leftLeg' },
    { from: 'AnkleLeft', to: 'FootLeft', region: 'leftLeg' },

    // Right leg
    { from: 'Pelvis', to: 'HipRight', region: 'rightLeg' },
    { from: 'HipRight', to: 'KneeRight', region: 'rightLeg' },
    { from: 'KneeRight', to: 'AnkleRight', region: 'rightLeg' },
    { from: 'AnkleRight', to: 'FootRight', region: 'rightLeg' },
];

/**
 * Body region groupings for color-coding and filtering.
 * Uses CANONICAL (Azure Kinect) names.
 */
export const BODY_REGIONS = {
    head: ['Head', 'Neck'],
    torso: ['Pelvis', 'SpineNaval', 'SpineChest'],
    leftArm: [
        'ClavicleLeft', 'ShoulderLeft', 'ElbowLeft',
        'WristLeft', 'HandLeft', 'HandTipLeft', 'ThumbLeft'
    ],
    rightArm: [
        'ClavicleRight', 'ShoulderRight', 'ElbowRight',
        'WristRight', 'HandRight', 'HandTipRight', 'ThumbRight'
    ],
    leftLeg: ['HipLeft', 'KneeLeft', 'AnkleLeft', 'FootLeft'],
    rightLeg: ['HipRight', 'KneeRight', 'AnkleRight', 'FootRight'],
};

/**
 * Body-region colours as EGDL token names (series palette), resolved per theme by
 * useSkeletonColors() in SkeletonStickFigure.js. Region colouring is used for joint
 * trajectories; a live/replay figure is drawn neutral (bone/joint/hand roles) or in its
 * player colour.
 */
export const REGION_COLORS = {
    head: 'series-5',
    torso: 'series-3',
    leftArm: 'series-1',
    rightArm: 'series-4',
    leftLeg: 'series-2',
    rightLeg: 'series-6',
};

/**
 * Default joint radius for sphere rendering (in scene units).
 */
export const DEFAULT_JOINT_RADIUS = 0.02;

/**
 * Default bone thickness for line rendering (in scene units).
 */
export const DEFAULT_BONE_THICKNESS = 0.015;

/**
 * All unique joint names used in the skeleton (CANONICAL / Azure Kinect).
 */
export const ALL_JOINTS = [
    'Pelvis',
    'SpineNaval',
    'SpineChest',
    'Neck',
    'Head',
    'ClavicleLeft',
    'ShoulderLeft',
    'ElbowLeft',
    'WristLeft',
    'HandLeft',
    'HandTipLeft',
    'ThumbLeft',
    'ClavicleRight',
    'ShoulderRight',
    'ElbowRight',
    'WristRight',
    'HandRight',
    'HandTipRight',
    'ThumbRight',
    'HipLeft',
    'KneeLeft',
    'AnkleLeft',
    'FootLeft',
    'HipRight',
    'KneeRight',
    'AnkleRight',
    'FootRight',
];

/**
 * Key joints for simplified visualization (reduce visual clutter).
 */
export const KEY_JOINTS = [
    'Head',
    'Neck',
    'ShoulderLeft',
    'ElbowLeft',
    'WristLeft',
    'HandLeft',
    'ShoulderRight',
    'ElbowRight',
    'WristRight',
    'HandRight',
    'HipLeft',
    'KneeLeft',
    'AnkleLeft',
    'HipRight',
    'KneeRight',
    'AnkleRight',
];

// ── Joint Name Aliases ──────────────────────────────────────────────────
// Maps alternative naming conventions → canonical (Azure Kinect) names.
// Supports Unity/Humanoid and Kinect v2 SDK naming.

/**
 * Maps alternative joint names → canonical Azure Kinect names.
 * Handles Unity/Humanoid and Kinect v2 SDK naming conventions.
 */
export const JOINT_ALIASES = {
    // ── Unity/Humanoid → Azure Kinect ──────────────────────────
    'Hips':           'Pelvis',
    'Spine':          'SpineNaval',
    'Chest':          'SpineChest',
    'UpperChest':     'SpineChest',      // Sometimes used in Unity

    // Left arm (Unity uses Side-first: LeftHand, LeftUpperArm)
    'LeftShoulder':   'ClavicleLeft',
    'LeftUpperArm':   'ShoulderLeft',
    'LeftLowerArm':   'ElbowLeft',
    'LeftHand':       'HandLeft',

    // Right arm
    'RightShoulder':  'ClavicleRight',
    'RightUpperArm':  'ShoulderRight',
    'RightLowerArm':  'ElbowRight',
    'RightHand':      'HandRight',

    // Left leg
    'LeftUpperLeg':   'HipLeft',
    'LeftLowerLeg':   'KneeLeft',
    'LeftFoot':       'AnkleLeft',
    'LeftToes':       'FootLeft',

    // Right leg
    'RightUpperLeg':  'HipRight',
    'RightLowerLeg':  'KneeRight',
    'RightFoot':      'AnkleRight',
    'RightToes':      'FootRight',

    // ── Kinect v2 SDK → Azure Kinect ──────────────────────────
    'SpineBase':      'Pelvis',
    'SpineMid':       'SpineNaval',
    'SpineShoulder':  'SpineChest',
};

/**
 * Maps Unity HumanBodyBones index (from boneIndex2MecanimMap in AvatarSkeleton.cs)
 * to canonical Azure Kinect joint names for live skeleton rendering.
 *
 * The indices match what NetworkSkeletonWebSocket sends in the "b" array:
 * Each bone has { i: index, p: [x,y,z], r: [rx,ry,rz,rw] }
 */
export const UNITY_BONE_INDEX_TO_JOINT = {
    0:  'Pelvis',           // HumanBodyBones.Hips
    1:  'SpineNaval',       // HumanBodyBones.Spine
    2:  'SpineChest',       // HumanBodyBones.Chest
    3:  'Neck',             // HumanBodyBones.Neck
    4:  'Head',             // HumanBodyBones.Head
    5:  'ShoulderLeft',     // HumanBodyBones.LeftUpperArm
    6:  'ElbowLeft',        // HumanBodyBones.LeftLowerArm
    7:  'HandLeft',         // HumanBodyBones.LeftHand
    8:  'HandTipLeft',      // HumanBodyBones.LeftIndexProximal (closest approx)
    9:  'HandTipLeft',      // HumanBodyBones.LeftIndexIntermediate
    10: 'ThumbLeft',        // HumanBodyBones.LeftThumbProximal
    11: 'ShoulderRight',    // HumanBodyBones.RightUpperArm
    12: 'ElbowRight',       // HumanBodyBones.RightLowerArm
    13: 'HandRight',        // HumanBodyBones.RightHand
    14: 'HandTipRight',     // HumanBodyBones.RightIndexProximal
    15: 'HandTipRight',     // HumanBodyBones.RightIndexIntermediate
    16: 'ThumbRight',       // HumanBodyBones.RightThumbProximal
    17: 'HipLeft',          // HumanBodyBones.LeftUpperLeg
    18: 'KneeLeft',         // HumanBodyBones.LeftLowerLeg
    19: 'AnkleLeft',        // HumanBodyBones.LeftFoot
    20: 'FootLeft',         // HumanBodyBones.LeftToes
    21: 'HipRight',         // HumanBodyBones.RightUpperLeg
    22: 'KneeRight',        // HumanBodyBones.RightLowerLeg
    23: 'AnkleRight',       // HumanBodyBones.RightFoot
    24: 'FootRight',        // HumanBodyBones.RightToes
};

/**
 * Convert a live skeleton frame from Unity format to joint position map.
 *
 * Supports two formats:
 *   1. Session skeleton (named joints from LiveSessionManager):
 *      { joints: { "Hips": {p:[x,y,z], r:[rx,ry,rz,rw]}, ... } }
 *      Joint names are Unity HumanBodyBones names, resolved via JOINT_ALIASES.
 *
 *   2. Bone-index format: { b: [[x,y,z], ...] } from egdl_Core LiveSession (index = bone),
 *      or the legacy { b: [{ i: 0, p: [x,y,z], r: [rx,ry,rz,rw] }, ...] }
 *
 * Output: { Pelvis: {x,y,z}, SpineNaval: {x,y,z}, ... }  (canonical names)
 */
export function liveFrameToJointPositions(frame) {
    if (!frame) return {};

    const result = {};

    // Format 1: Named joints from session:skeleton
    if (frame.joints) {
        for (const [name, joint] of Object.entries(frame.joints)) {
            if (!joint || !joint.p) continue;
            // Skip sentinel values
            if (joint.p[0] === 111 && joint.p[2] === 333) continue;
            // Resolve Unity name → canonical name (e.g., "Hips" → "Pelvis")
            const canonical = JOINT_ALIASES[name] || name;
            result[canonical] = { x: joint.p[0], y: joint.p[1], z: joint.p[2] };
        }
        return result;
    }

    // Format 2: Legacy bone-index array from skeleton:data
    if (frame.b) {
        frame.b.forEach((bone, idx) => {
            // egdl_Core LiveSession sends b: [[x,y,z], …] (array index = bone index);
            // the older NetworkSkeletonWebSocket sent b: [{ i, p: [x,y,z], r }].
            const isArr = Array.isArray(bone);
            const p = isArr ? bone : bone && bone.p;
            const jointName = UNITY_BONE_INDEX_TO_JOINT[isArr ? idx : bone && bone.i];
            if (!jointName || !p || p.length < 3) return;
            if (p[0] === 111 && p[2] === 333) return;
            if (!result[jointName]) result[jointName] = { x: p[0], y: p[1], z: p[2] };
        });
        return result;
    }

    return result;
};

// Build a reverse alias map: canonical → [aliases]
const _reverseAliases = {};
Object.entries(JOINT_ALIASES).forEach(([alias, canonical]) => {
    if (!_reverseAliases[canonical]) _reverseAliases[canonical] = [];
    _reverseAliases[canonical].push(alias);
});

/**
 * Resolve a joint name to its canonical form.
 * Returns the canonical name if the input is an alias, or the input itself
 * if it's already canonical or unknown.
 *
 * @param {string} name - Joint name from API data (any convention)
 * @returns {string} Canonical (Azure Kinect) joint name
 */
export function resolveJointName(name) {
    if (!name) return '';
    // Already canonical?
    if (ALL_JOINTS.includes(name)) return name;
    // Check alias table
    if (JOINT_ALIASES[name]) return JOINT_ALIASES[name];
    // Unknown — return as-is
    return name;
}

/**
 * Get the region colour token for a body part (canonical names only).
 *
 * @param {string} bodyPart - Canonical joint name
 * @returns {string|null} Token name (e.g. 'series-1'); resolve with useSkeletonColors()
 */
export function getBodyPartColor(bodyPart) {
    for (const [region, parts] of Object.entries(BODY_REGIONS)) {
        if (parts.includes(bodyPart)) {
            return REGION_COLORS[region];
        }
    }
    return null; // unknown part
}

/**
 * Region colour token for a body part, with alias resolution.
 *
 * @param {string} bodyPart - Joint name (any naming convention)
 * @returns {string|null} Token name
 */
export function getBodyPartColorNormalized(bodyPart) {
    // Try raw name first
    const direct = getBodyPartColor(bodyPart);
    if (direct) return direct;
    // Try resolved alias
    const canonical = resolveJointName(bodyPart);
    return getBodyPartColor(canonical);
}

/**
 * Get the region name for a specific body part.
 * @param {string} bodyPart - Joint name (any naming convention)
 * @returns {string|null} Region name or null if not found
 */
export function getBodyPartRegion(bodyPart) {
    const canonical = resolveJointName(bodyPart);
    for (const [region, parts] of Object.entries(BODY_REGIONS)) {
        if (parts.includes(canonical)) {
            return region;
        }
    }
    return null;
}

/**
 * Build a bidirectional name map from actual data joint names to canonical names.
 * Handles any naming convention via the JOINT_ALIASES table.
 *
 * @param {string[]} actualNames - Joint names as they appear in the API data
 * @returns {{ toCanonical: Object, toActual: Object }}
 *   toCanonical: { actualDataName → canonicalName }
 *   toActual:    { canonicalName → actualDataName }
 */
export function buildJointNameMap(actualNames) {
    const toCanonical = {};
    const toActual = {};

    actualNames.forEach(name => {
        const canonical = resolveJointName(name);
        toCanonical[name] = canonical;
        // Map canonical back to actual data name (last one wins if duplicates)
        toActual[canonical] = name;
    });

    return { toCanonical, toActual };
}
